(function () {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let ytPreloaded = false;

  function preloadYouTube() {
    if (ytPreloaded) return;
    ytPreloaded = true;
    const preconnects = [
      'https://www.youtube-nocookie.com',
      'https://i.ytimg.com',
      'https://yt3.ggpht.com',
      'https://fonts.gstatic.com'
    ];
    preconnects.forEach((href) => {
      const link = document.createElement('link');
      link.rel = 'preconnect';
      link.href = href;
      link.crossOrigin = '';
      document.head.appendChild(link);
    });
  }

  function initSlider(root) {
    const track = root.querySelector('.block__main-hero-video-slider-wrapper');
    const nextBtn = root.querySelector('.block__main-hero-video-slider-next');
    const prevBtn = root.querySelector('.block__main-hero-video-slider-prev');
    if (!track) return;

    function step() {
      const slide = track.querySelector('.block__main-hero-video-slider');
      if (!slide) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      return slide.getBoundingClientRect().width + gap;
    }

    function nudge(dir) {
      const max = track.scrollWidth - track.clientWidth;
      if (max <= 1) return;
      const atEnd = track.scrollLeft >= max - 1;
      const atStart = track.scrollLeft <= 1;
      let target = track.scrollLeft + dir * step();
      if (dir > 0 && atEnd) target = 0;
      else if (dir < 0 && atStart) target = max;
      track.scrollTo({ left: target, behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    nextBtn && nextBtn.addEventListener('click', () => nudge(1));
    prevBtn && prevBtn.addEventListener('click', () => nudge(-1));

    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); nudge(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-1); }
      else if (e.key === 'Home') { e.preventDefault(); track.scrollTo({ left: 0, behavior: reduceMotion ? 'auto' : 'smooth' }); }
      else if (e.key === 'End') { e.preventDefault(); track.scrollTo({ left: track.scrollWidth, behavior: reduceMotion ? 'auto' : 'smooth' }); }
    });

    // Pointer drag (mouse only — touch uses native momentum scroll)
    const DRAG_THRESHOLD = 6;
    let dragging = false;
    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let velocity = 0;
    let lastX = 0;
    let lastT = 0;

    let pointerId = null;

    track.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      dragging = true;
      moved = false;
      pointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      lastX = e.clientX;
      lastT = performance.now();
      velocity = 0;
      // NOTE: do NOT setPointerCapture here — it would retarget pointerup/click
      // and buttons inside the track would stop firing click. Capture only after
      // the drag threshold is crossed.
    });

    track.addEventListener('pointermove', (e) => {
      if (!dragging || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < DRAG_THRESHOLD) return;
      if (!moved) {
        moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(e.pointerId); } catch (err) {}
      }
      const now = performance.now();
      const dt = now - lastT;
      if (dt > 0) velocity = (e.clientX - lastX) / dt;
      lastX = e.clientX;
      lastT = now;
      track.scrollLeft = startScroll - dx;
      e.preventDefault();
    });

    function endDrag(e) {
      if (!dragging) return;
      dragging = false;
      if (moved) {
        try { track.releasePointerCapture(e.pointerId); } catch (err) {}
        // Fling — extrapolate velocity into a short smooth scroll
        const flingPx = Math.max(-600, Math.min(600, -velocity * 180));
        if (Math.abs(flingPx) > 20 && !reduceMotion) {
          track.scrollBy({ left: flingPx, behavior: 'smooth' });
        }
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            track.classList.remove('is-dragging');
            moved = false;
          });
        });
      }
      pointerId = null;
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', endDrag);

    // Swallow the synthetic click that fires after a drag so we don't open the
    // modal. A simple click never toggles `moved`, so it passes through.
    track.addEventListener('click', (e) => {
      if (moved || track.classList.contains('is-dragging')) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // Warm up YT connections the first time the user shows intent
    ['pointerenter', 'focusin', 'touchstart'].forEach((ev) => {
      root.addEventListener(ev, preloadYouTube, { once: true, passive: true });
    });

    // Track active slide for aria-current (progressive enhancement)
    if ('IntersectionObserver' in window) {
      const slides = track.querySelectorAll('.block__main-hero-video-slider');
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.6) {
            entry.target.setAttribute('aria-current', 'true');
          } else {
            entry.target.removeAttribute('aria-current');
          }
        });
      }, { root: track, threshold: [0, 0.6, 1] });
      slides.forEach((s) => io.observe(s));
    }
  }

  function initModal() {
    const modal = document.querySelector('.block__main-hero-video-modal');
    if (!modal) return;
    const body = modal.querySelector('.block__main-hero-video-modal-body');
    const loading = modal.querySelector('.block__main-hero-video-loading');
    const closeBtn = modal.querySelector('.block__main-hero-video-modal-close');
    const hasDialog = typeof modal.showModal === 'function';
    let lastTrigger = null;

    function showLoading() { if (loading) loading.style.display = ''; }
    function hideLoading() { if (loading) loading.style.display = 'none'; }

    function clearIframe() {
      const iframe = body.querySelector('iframe');
      if (iframe) iframe.remove();
      showLoading();
    }

    function injectIframe(videoId) {
      clearIframe();
      const iframe = document.createElement('iframe');
      iframe.title = 'YouTube video player';
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute(
        'allow',
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
      );
      iframe.setAttribute('allowfullscreen', '');
      iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      iframe.addEventListener('load', hideLoading, { once: true });
      iframe.src =
        'https://www.youtube-nocookie.com/embed/' +
        encodeURIComponent(videoId) +
        '?autoplay=1&controls=1&modestbranding=1&rel=0&iv_load_policy=3&playsinline=1';
      body.appendChild(iframe);
    }

    function openModal(videoId, trigger) {
      if (!videoId) return;
      preloadYouTube();
      lastTrigger = trigger || null;
      injectIframe(videoId);
      if (hasDialog) {
        try { modal.showModal(); } catch (e) { modal.setAttribute('open', ''); }
      } else {
        modal.setAttribute('open', '');
      }
      document.documentElement.classList.add('is-video-modal-open');
    }

    function closeModal() {
      clearIframe();
      if (hasDialog && modal.open) modal.close();
      else modal.removeAttribute('open');
      document.documentElement.classList.remove('is-video-modal-open');
      if (lastTrigger && typeof lastTrigger.focus === 'function') {
        lastTrigger.focus({ preventScroll: true });
        lastTrigger = null;
      }
    }

    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
    modal.addEventListener('close', () => {
      clearIframe();
      document.documentElement.classList.remove('is-video-modal-open');
    });
    modal.addEventListener('cancel', (e) => {
      e.preventDefault();
      closeModal();
    });
    closeBtn && closeBtn.addEventListener('click', closeModal);

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('.block__main-hero-video-slider[data-iframe]');
      if (!trigger) return;
      e.preventDefault();
      openModal(trigger.dataset.iframe, trigger);
    });

    function openModalFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const videoId = params.get('video');
      if (!videoId) return;
      let trigger = null;
      try {
        trigger = document.querySelector(
          '.block__main-hero-video-slider[data-iframe="' + CSS.escape(videoId) + '"]'
        );
      } catch (e) {}
      if (trigger && typeof trigger.scrollIntoView === 'function') {
        trigger.scrollIntoView({ block: 'nearest', inline: 'center' });
      }
      openModal(videoId, trigger);
    }

    openModalFromUrl();
  }

  function init() {
    document.querySelectorAll('.block__main-hero-video-slider-inner').forEach(initSlider);
    initModal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
